import { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Spin, Empty, message } from 'antd';
import ReactEChartsCore from 'echarts-for-react';
import { vppMarket } from '../../../services/api';

function formatTime(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('zh-CN', { hour12: false });
}

export default function VppMarket() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    Promise.all([vppMarket.overview(), vppMarket.list()])
      .then(([ovRes, listRes]) => {
        setOverview(ovRes.data);
        setTransactions((listRes.data || []).map(t => ({
          key: t.id,
          id: t.external_id || `MKT${t.id}`,
          type: t.market_type || t.transaction_type,
          volume: parseFloat(t.quantity || 0),
          price: parseFloat(t.price || 0),
          amount: parseFloat(t.amount || 0),
          time: formatTime(t.transaction_time),
          rawTime: t.transaction_time,
          status: t.status === '待提交' ? '申报中' : t.status === '已成交' ? '成交' : t.status,
        })));
      })
      .catch(err => message.error(err.message || '加载市场数据失败'))
      .finally(() => setLoading(false));
  }, []);

  // 成交价格曲线：由真实交易记录按时间绘制；无成交记录时展示「暂无市场数据」
  const priced = transactions
    .filter(t => t.rawTime && t.price > 0)
    .sort((a, b) => new Date(a.rawTime) - new Date(b.rawTime));
  const hasPriceCurve = priced.length > 0;
  const priceOption = hasPriceCurve ? {
    tooltip: { trigger: 'axis' },
    legend: { data: ['成交价'] },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: priced.map(t => t.time) },
    yAxis: { type: 'value', name: '元/kWh' },
    series: [
      { name: '成交价', type: 'line', smooth: true, data: priced.map(t => t.price), itemStyle: { color: '#1890ff' } },
    ]
  } : {};

  const columns = [
    { title: '交易编号', dataIndex: 'id' },
    { title: '类型', dataIndex: 'type', render: t => <Tag>{t}</Tag> },
    { title: '电量(kWh)', dataIndex: 'volume', render: v => v.toLocaleString() },
    { title: '价格(元/kWh)', dataIndex: 'price', render: v => v.toFixed(3) },
    { title: '总金额(元)', dataIndex: 'amount', render: v => v.toLocaleString() },
    { title: '交易时间', dataIndex: 'time' },
    { title: '状态', dataIndex: 'status', render: v => <Tag color={v === '成交' ? 'green' : v === '申报中' ? 'blue' : 'default'}>{v}</Tag> },
  ];

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  if (!overview) return <div>暂无数据</div>;

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col span={6}><Card size="small"><Statistic title="日前均价" value={overview.day_ahead_price.flat ?? '—'} suffix={overview.day_ahead_price.flat != null ? '元/kWh' : ''} precision={3} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title={`实时均价${overview.real_time_price.period ? `（${overview.real_time_price.period}）` : ''}`} value={overview.real_time_price.current ?? '—'} suffix={overview.real_time_price.current != null ? '元/kWh' : ''} precision={3} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="调频服务费" value={overview.ancillary_service.fm_price ?? '—'} suffix={overview.ancillary_service.fm_price != null ? '元/MW' : ''} precision={1} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="碳价" value={overview.carbon_price.current ?? '—'} suffix={overview.carbon_price.current != null ? '元/吨' : ''} precision={1} valueStyle={{ color: '#52c41a' }} /></Card></Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}><Card title="市场成交价格（真实交易记录）" size="small">
          {hasPriceCurve
            ? <ReactEChartsCore option={priceOption} style={{ height: 280 }} />
            : <Empty description="暂无市场数据" style={{ padding: '60px 0' }} />}
        </Card></Col>
      </Row>
      <Card title="交易记录" style={{ marginTop: 16 }} size="small">
        <Table columns={columns} dataSource={transactions} pagination={false} size="small" locale={{ emptyText: '暂无交易记录' }} />
      </Card>
    </div>
  );
}
